import { useToast } from "@/components/ui/use-toast";
import { useCallback, useState } from "react";

export interface StandardError {
  code?: string;
  message: string;
  details?: any;
  severity: 'error' | 'warning' | 'info';
}

/**
 * Standardized error handling hook for admin components
 */
export const useStandardizedError = () => {
  const { toast } = useToast();

  const handleError = useCallback((error: unknown, context?: string): StandardError => {
    let standardError: StandardError;

    // Parse different error types
    if (error instanceof Error) {
      standardError = {
        message: error.message,
        severity: 'error'
      };
    } else if (typeof error === 'string') {
      standardError = {
        message: error,
        severity: 'error'
      };
    } else if (error && typeof error === 'object' && 'message' in error) {
      standardError = {
        message: (error as any).message || 'Unknown error',
        code: (error as any).code,
        details: error,
        severity: 'error'
      };
    } else {
      standardError = {
        message: 'An unexpected error occurred',
        severity: 'error'
      };
    }

    // Enhanced error messages based on common patterns
    if (standardError.message.includes('duplicate key value')) {
      standardError.message = 'Este item já existe. Por favor, use um nome diferente.';
      standardError.code = 'DUPLICATE_ENTRY';
    } else if (standardError.message.includes('foreign key constraint')) {
      standardError.message = 'Não é possível eliminar este item pois está a ser utilizado noutro local.';
      standardError.code = 'CONSTRAINT_VIOLATION';
    } else if (standardError.message.includes('connection')) {
      standardError.message = 'Problema de conexão. Por favor, tente novamente.';
      standardError.code = 'CONNECTION_ERROR';
    } else if (standardError.message.includes('permission') || standardError.message.includes('unauthorized')) {
      standardError.message = 'Não tem permissão para realizar esta ação.';
      standardError.code = 'PERMISSION_DENIED';
      standardError.severity = 'warning';
    }

    // Log error for debugging
    console.error(`[${context || 'Admin'}] Error:`, {
      message: standardError.message,
      code: standardError.code,
      originalError: error,
      timestamp: new Date().toISOString()
    });

    // Show user-friendly toast
    toast({
      title: "Erro",
      description: standardError.message,
      variant: standardError.severity === 'warning' ? 'default' : 'destructive',
    });

    return standardError;
  }, [toast]);

  const handleSuccess = useCallback((message: string, details?: any) => {
    console.log(`[Admin] Success: ${message}`, details);
    toast({
      title: "Sucesso",
      description: message,
      variant: 'default',
    });
  }, [toast]);

  const handleWarning = useCallback((message: string, details?: any) => {
    console.warn(`[Admin] Warning: ${message}`, details);
    toast({
      title: "Aviso",
      description: message,
      variant: 'default',
    });
  }, [toast]);

  return {
    handleError,
    handleSuccess,
    handleWarning
  };
};

/**
 * Standardized loading state management
 */
export const useStandardizedLoading = () => {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

  const setLoading = useCallback((key: string, loading: boolean) => {
    setLoadingStates(prev => ({
      ...prev,
      [key]: loading
    }));
  }, []);

  const isLoading = useCallback((key: string) => {
    return loadingStates[key] || false;
  }, [loadingStates]);

  const isAnyLoading = useCallback(() => {
    return Object.values(loadingStates).some(Boolean);
  }, [loadingStates]);

  return {
    setLoading,
    isLoading,
    isAnyLoading,
    loadingStates
  };
};

